import React, { useEffect, useState, useCallback } from "react";
import { api, apiError, fileUrl } from "@/lib/api";
import AlumniLayout from "@/components/AlumniLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Briefcase, Building2, GraduationCap, Users2, Search, MapPin, Handshake,
  Send, Plus, Trash2, ExternalLink, Sparkles, X, Filter, ChevronDown,
} from "lucide-react";

const TABS = [
  { key: "network", label: "Network", icon: Building2 },
  { key: "mentors", label: "Mentors", icon: GraduationCap },
  { key: "jobs", label: "Job Board", icon: Briefcase },
];

const Initials = ({ name }) => (
  <span className="text-sm font-semibold text-primary">{(name || "?").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase()}</span>
);

const Avatar = ({ m }) => (
  <div className="h-11 w-11 rounded-full bg-accent/20 flex items-center justify-center overflow-hidden shrink-0 border border-border">
    {m.photo ? <img src={fileUrl(m.photo)} alt={m.name} className="h-full w-full object-cover" loading="lazy" /> : <Initials name={m.name} />}
  </div>
);

/* ---------------- Preferences panel ---------------- */
function PrefsPanel() {
  const [c, setC] = useState(null);
  const [topicInput, setTopicInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(() => localStorage.getItem("ud_career_prefs_open") !== "false");

  useEffect(() => { localStorage.setItem("ud_career_prefs_open", String(open)); }, [open]);
  useEffect(() => { api.get("/me/career").then((r) => setC(r.data)).catch(() => setC({ open_to_networking: false, open_to_mentoring: false, seeking_mentor: false, mentor_topics: [], headline: "" })); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.patch("/me/career", patch);
      setC(r.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  if (!c) return null;
  const Toggle = ({ k, label, hint }) => (
    <label className="flex items-start gap-3 py-2 cursor-pointer" data-testid={`career-toggle-${k}`}>
      <input type="checkbox" checked={!!c[k]} onChange={(e) => save({ [k]: e.target.checked })} disabled={saving} className="mt-1 h-4 w-4 accent-[var(--primary)]" />
      <span><span className="text-sm font-medium text-foreground">{label}</span><span className="block text-xs text-muted-foreground">{hint}</span></span>
    </label>
  );
  const addTopic = () => {
    const t = topicInput.trim();
    if (!t) return;
    if (c.mentor_topics.map((x) => x.toLowerCase()).includes(t.toLowerCase())) { setTopicInput(""); return; }
    save({ mentor_topics: [...c.mentor_topics, t] });
    setTopicInput("");
  };

  return (
    <div data-testid="career-prefs" className="bg-card border border-border rounded-lg p-5 mb-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /><h3 className="font-serif text-lg">Your networking preferences</h3></div>
        <button data-testid="career-prefs-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open} title={open ? "Collapse" : "Expand"}
          className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
          <ChevronDown className={`h-5 w-5 transition-transform ${open ? "" : "-rotate-90"}`} />
        </button>
      </div>
      {open && <>
      <p className="text-xs text-muted-foreground mb-3">Choose how fellow alumni can find and reach you professionally. You can change this anytime.</p>
      <div className="grid sm:grid-cols-3 gap-x-6 divide-y sm:divide-y-0 divide-border">
        <Toggle k="open_to_networking" label="Open to connecting" hint="Show me in the professional network" />
        <Toggle k="open_to_mentoring" label="Offer mentorship" hint="List me as a mentor others can request" />
        <Toggle k="seeking_mentor" label="Looking for a mentor" hint="I'd like guidance from an alumnus" />
      </div>
      <div className="mt-4">
        <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Professional headline</label>
        <input data-testid="career-headline" defaultValue={c.headline} onBlur={(e) => e.target.value.trim() !== (c.headline || "") && save({ headline: e.target.value })}
          placeholder="e.g. Partner at Baker Botts · Corporate Law" maxLength={160}
          className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-sm" />
      </div>
      {(c.open_to_mentoring || c.seeking_mentor) && (
        <div className="mt-4">
          <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Topics you can advise on / want help with</label>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {c.mentor_topics.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-accent/20 text-primary">
                {t}<button onClick={() => save({ mentor_topics: c.mentor_topics.filter((x) => x !== t) })} className="hover:text-destructive" data-testid={`topic-remove-${t}`}><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <input data-testid="career-topic-input" value={topicInput} onChange={(e) => setTopicInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTopic())}
              placeholder="Add a topic (e.g. Career changes, Law school, Startups)" className="flex-1 px-3 py-2 rounded-md border border-border bg-background text-sm" />
            <button data-testid="career-topic-add" onClick={addTopic} className="px-3 py-2 rounded-md border border-border text-sm hover:bg-muted flex items-center gap-1"><Plus className="h-4 w-4" /> Add</button>
          </div>
        </div>
      )}
      </>}
    </div>
  );
}

/* ---------------- Reach-out / mentor-request modal ---------------- */
function ContactModal({ member, mode, onClose }) {
  const [msg, setMsg] = useState(mode === "mentor"
    ? `Hi ${member.name?.split(" ")[0] || "there"},\n\nI'm a fellow alumnus and would love your guidance on `
    : `Hi ${member.name?.split(" ")[0] || "there"},\n\nI came across your profile in the alumni network and would love to connect.`);
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!msg.trim()) { toast.error("Please write a short message"); return; }
    setSending(true);
    try {
      const url = mode === "mentor" ? `/experience/alumni/${member.id}/mentor-request` : `/experience/alumni/${member.id}/reach-out`;
      await api.post(url, { message: msg });
      toast.success(mode === "mentor" ? "Mentorship request sent privately" : "Message sent privately");
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not send"); }
    finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="career-contact-modal">
        <DialogHeader>
          <DialogTitle className="font-serif">{mode === "mentor" ? "Request mentorship" : "Connect"} with {member.name}</DialogTitle>
          <DialogDescription>Your message is relayed privately — your email address is never shared. They can reply, ignore, or block.</DialogDescription>
        </DialogHeader>
        <textarea data-testid="career-contact-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={7} className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm hover:bg-muted">Cancel</button>
          <button data-testid="career-contact-send" onClick={send} disabled={sending} className="px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2"><Send className="h-4 w-4" /> {sending ? "Sending…" : "Send"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Member card ---------------- */
function MemberCard({ m, onContact, mentor }) {
  const car = m.career || {};
  return (
    <div data-testid={`career-member-${m.id}`} className="bg-card border border-border rounded-lg p-4 hover:shadow-md hover:border-primary/40 transition-all">
      <div className="flex items-start gap-3">
        <Avatar m={m} />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-foreground truncate">{m.name} {m.class_year ? <span className="text-xs text-muted-foreground">'{String(m.class_year).slice(-2)}</span> : null}</div>
          {car.headline ? <div className="text-xs text-muted-foreground line-clamp-2">{car.headline}</div>
            : (m.title || m.employer) ? <div className="text-xs text-muted-foreground truncate">{[m.title, m.employer].filter(Boolean).join(" · ")}</div> : null}
          <div className="flex items-center gap-2 mt-1 flex-wrap text-[11px] text-muted-foreground">
            {m.industry && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted"><Building2 className="h-3 w-3" />{m.industry}</span>}
            {(m.city || m.state) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{[m.city, m.state].filter(Boolean).join(", ")}</span>}
          </div>
        </div>
      </div>
      {mentor && car.mentor_topics?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {car.mentor_topics.slice(0, 4).map((t) => <span key={t} className="text-[11px] px-1.5 py-0.5 rounded-full bg-accent/20 text-primary">{t}</span>)}
        </div>
      )}
      <div className="flex items-center gap-2 mt-3">
        {mentor && (
          <button data-testid={`career-request-mentor-${m.id}`} onClick={() => onContact(m, "mentor")} disabled={!m.has_email}
            className="flex-1 flex items-center justify-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed">
            <GraduationCap className="h-4 w-4" /> Request mentorship
          </button>
        )}
        {!mentor && (
          <button data-testid={`career-connect-${m.id}`} onClick={() => onContact(m, "connect")} disabled={!m.has_email}
            className="flex-1 flex items-center justify-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed">
            <Handshake className="h-4 w-4" /> Connect
          </button>
        )}
        {m.linkedin && <a href={m.linkedin} target="_blank" rel="noreferrer" title="LinkedIn" className="p-1.5 rounded-md border border-border hover:bg-muted"><ExternalLink className="h-4 w-4" /></a>}
      </div>
      {!m.has_email && <div className="text-[11px] text-muted-foreground mt-1.5">No contact channel available</div>}
    </div>
  );
}

/* ---------------- Job posting ---------------- */
function JobForm({ onCreated, onClose }) {
  const [f, setF] = useState({ title: "", company: "", location: "", remote: false, type: "Full-time", industry: "", description: "", apply_url: "", apply_email: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.post("/experience/jobs", f);
      toast.success("Job posted");
      onCreated(r.data);
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not post job"); }
    finally { setSaving(false); }
  };
  const inp = "w-full px-3 py-2 rounded-md border border-border bg-background text-sm";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="job-form-modal">
        <DialogHeader><DialogTitle className="font-serif">Post a job or internship</DialogTitle><DialogDescription>Share an opening with the alumni community.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground">Title *</label><input data-testid="job-title" className={inp} value={f.title} onChange={(e) => set("title", e.target.value)} /></div>
            <div><label className="text-xs text-muted-foreground">Company *</label><input data-testid="job-company" className={inp} value={f.company} onChange={(e) => set("company", e.target.value)} /></div>
            <div><label className="text-xs text-muted-foreground">Location</label><input data-testid="job-location" className={inp} value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="City, ST" /></div>
            <div><label className="text-xs text-muted-foreground">Type</label>
              <select data-testid="job-type" className={inp} value={f.type} onChange={(e) => set("type", e.target.value)}>
                {["Full-time", "Part-time", "Internship", "Contract", "Volunteer"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div><label className="text-xs text-muted-foreground">Industry</label><input data-testid="job-industry" className={inp} value={f.industry} onChange={(e) => set("industry", e.target.value)} /></div>
            <label className="flex items-center gap-2 text-sm mt-6"><input type="checkbox" data-testid="job-remote" checked={f.remote} onChange={(e) => set("remote", e.target.checked)} className="h-4 w-4 accent-[var(--primary)]" /> Remote friendly</label>
          </div>
          <div><label className="text-xs text-muted-foreground">Description</label><textarea data-testid="job-description" rows={5} className={inp} value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground">Application link</label><input data-testid="job-apply-url" className={inp} value={f.apply_url} onChange={(e) => set("apply_url", e.target.value)} placeholder="https://…" /></div>
            <div><label className="text-xs text-muted-foreground">or Application email</label><input data-testid="job-apply-email" className={inp} value={f.apply_email} onChange={(e) => set("apply_email", e.target.value)} placeholder="jobs@company.com" /></div>
          </div>
          <p className="text-xs text-muted-foreground">Add at least one way to apply.</p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm hover:bg-muted">Cancel</button>
          <button data-testid="job-submit" onClick={submit} disabled={saving} className="px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">{saving ? "Posting…" : "Post job"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function JobCard({ j, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`job-card-${j.id}`} className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-serif text-lg leading-snug text-foreground">{j.title}</div>
          <div className="text-sm text-muted-foreground flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{j.company}</div>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-accent/20 text-primary shrink-0">{j.type}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-muted-foreground">
        {(j.location || j.remote) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{[j.location, j.remote ? "Remote" : null].filter(Boolean).join(" · ")}</span>}
        {j.industry && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted">{j.industry}</span>}
      </div>
      {j.description && <p className={`text-sm text-foreground/80 mt-2 whitespace-pre-line ${open ? "" : "line-clamp-3"}`}>{j.description}</p>}
      {j.description && j.description.length > 180 && <button onClick={() => setOpen((o) => !o)} className="text-xs text-primary mt-1">{open ? "Show less" : "Show more"}</button>}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
        <span className="text-[11px] text-muted-foreground">Posted by {j.posted_by_name}{j.posted_by_role === "staff" ? " (Alumni Office)" : ""}</span>
        <div className="flex items-center gap-2">
          {j.can_manage && <button data-testid={`job-delete-${j.id}`} onClick={() => onDelete(j)} className="p-1.5 rounded-md text-destructive hover:bg-destructive/10" title="Remove"><Trash2 className="h-4 w-4" /></button>}
          {(j.apply_url || j.apply_email) && (
            <a data-testid={`job-apply-${j.id}`} href={j.apply_url || `mailto:${j.apply_email}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-primary text-white hover:bg-primary/90">Apply <ExternalLink className="h-3.5 w-3.5" /></a>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function CareerNetwork() {
  const [tab, setTab] = useState("network");
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("");
  const [topic, setTopic] = useState("");
  const [data, setData] = useState({ members: [], industries: [], top_employers: [] });
  const [mentorsData, setMentorsData] = useState({ mentors: [], topics: [] });
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState(null);
  const [showJobForm, setShowJobForm] = useState(false);

  const loadNetwork = useCallback(() => {
    setLoading(true);
    api.get(`/experience/careers?q=${encodeURIComponent(q)}&industry=${encodeURIComponent(industry)}`)
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [q, industry]);
  const loadMentors = useCallback(() => {
    setLoading(true);
    api.get(`/experience/mentors?q=${encodeURIComponent(q)}&topic=${encodeURIComponent(topic)}`)
      .then((r) => setMentorsData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [q, topic]);
  const loadJobs = useCallback(() => {
    setLoading(true);
    api.get(`/experience/jobs?q=${encodeURIComponent(q)}`).then((r) => setJobs(r.data.jobs)).catch(() => {}).finally(() => setLoading(false));
  }, [q]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (tab === "network") loadNetwork();
      else if (tab === "mentors") loadMentors();
      else loadJobs();
    }, 250);
    return () => clearTimeout(t);
  }, [tab, loadNetwork, loadMentors, loadJobs]);

  const deleteJob = async (j) => {
    if (!window.confirm(`Remove "${j.title}"?`)) return;
    try { await api.delete(`/experience/jobs/${j.id}`); setJobs((p) => p.filter((x) => x.id !== j.id)); toast.success("Removed"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not remove"); }
  };

  return (
    <AlumniLayout wide>
      <div className="mb-5">
        <div className="flex items-center gap-2 text-primary"><Briefcase className="h-6 w-6" /><h1 className="font-serif text-3xl">Business &amp; Career Network</h1></div>
        <p className="text-sm text-muted-foreground mt-1">Connect with alumni across industries, find a mentor, and discover job opportunities.</p>
      </div>

      <PrefsPanel />

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-border mb-4">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} data-testid={`career-tab-${t.key}`} onClick={() => { setTab(t.key); setQ(""); setIndustry(""); setTopic(""); }}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === t.key ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Search row */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input data-testid="career-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={tab === "jobs" ? "Search jobs, companies…" : "Search by name, employer, headline…"}
            className="w-full pl-9 pr-3 py-2 rounded-md border border-border bg-background text-sm" />
        </div>
        {tab === "network" && data.industries.length > 0 && (
          <select data-testid="career-industry-filter" value={industry} onChange={(e) => setIndustry(e.target.value)} className="px-3 py-2 rounded-md border border-border bg-background text-sm">
            <option value="">All industries</option>
            {data.industries.map((i) => <option key={i.name} value={i.name}>{i.name} ({i.count})</option>)}
          </select>
        )}
        {tab === "mentors" && mentorsData.topics.length > 0 && (
          <select data-testid="career-topic-filter" value={topic} onChange={(e) => setTopic(e.target.value)} className="px-3 py-2 rounded-md border border-border bg-background text-sm">
            <option value="">All topics</option>
            {mentorsData.topics.map((t) => <option key={t.name} value={t.name}>{t.name} ({t.count})</option>)}
          </select>
        )}
        {tab === "jobs" && (
          <button data-testid="career-post-job" onClick={() => setShowJobForm(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90"><Plus className="h-4 w-4" /> Post a job</button>
        )}
      </div>

      {loading && <div className="text-sm text-muted-foreground py-10 text-center">Loading…</div>}

      {!loading && tab === "network" && (
        data.members.length === 0
          ? <Empty icon={Users2} text="No alumni match your filters yet." />
          : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="career-network-grid">
              {data.members.map((m) => <MemberCard key={m.id} m={m} mentor={false} onContact={(mem, mode) => setContact({ member: mem, mode })} />)}
            </div>
      )}

      {!loading && tab === "mentors" && (
        mentorsData.mentors.length === 0
          ? <Empty icon={GraduationCap} text="No mentors are listed yet. Be the first — toggle “Offer mentorship” above." />
          : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="career-mentors-grid">
              {mentorsData.mentors.map((m) => <MemberCard key={m.id} m={m} mentor onContact={(mem, mode) => setContact({ member: mem, mode })} />)}
            </div>
      )}

      {!loading && tab === "jobs" && (
        jobs.length === 0
          ? <Empty icon={Briefcase} text="No open postings yet. Share the first opening with the community." />
          : <div className="grid sm:grid-cols-2 gap-3" data-testid="career-jobs-grid">
              {jobs.map((j) => <JobCard key={j.id} j={j} onDelete={deleteJob} />)}
            </div>
      )}

      {contact && <ContactModal member={contact.member} mode={contact.mode} onClose={() => setContact(null)} />}
      {showJobForm && <JobForm onCreated={(j) => setJobs((p) => [j, ...p])} onClose={() => setShowJobForm(false)} />}
    </AlumniLayout>
  );
}

const Empty = ({ icon: Icon, text }) => (
  <div className="text-center py-16 text-muted-foreground" data-testid="career-empty">
    <Icon className="h-10 w-10 mx-auto mb-3 opacity-40" />
    <p className="text-sm">{text}</p>
  </div>
);
